import React, { useState, useMemo, useCallback } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { View, Text, ScrollView, Pressable, Image } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useRouter } from 'expo-router';
import { useAuth } from '@/lib/supabase';
import {
  User,
  Building2,
  MapPin,
  Target,
  DollarSign,
  Briefcase,
  Settings,
  LogOut,
  ChevronRight,
  Edit3,
  Crown,
  Gift,
  Linkedin,
  CheckCircle,
  Calendar,
  TrendingUp,
  Plus,
  Zap,
  FileSearch,
  Clock,
  Globe,
  Users,
  Sparkles,
  Star,
  Twitter,
  Quote,
  Timer,
  HandHelping,
  History,
  BookOpen,
  XCircle,
  Pencil,
  ShieldCheck,
  Inbox,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import { formatCurrency } from '@/lib/utils/format';
import { getMockEnhancedInvestorProfile } from '@/lib/state/mock-data-provider';
import { mode } from '@/lib/config/app-mode';
import { capitalizeWords } from '@/lib/format';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { useAlert } from '@/components/AlertDialog';
import { SectionDivider } from '@/components/SectionDivider';
import Badge from '@/components/Badge';
import StarRating from '@/components/StarRating';
import TestimonialCard from '@/components/TestimonialCard';
import ProfileEditModal from '@/components/ProfileEditModal';
import SubmissionLinkSettings from '@/components/investor/SubmissionLinkSettings';
import { useUpdateInvestorProfile } from '@/lib/supabase/investor-queries';
import {
  buildInvestorProfileDbUpdate,
  buildInvestorNameUpdate,
  type InvestorEditSection,
} from '@/lib/investor/profile-edit-fields';
import { useTrackRecord } from '@/lib/supabase/track-record';
import {
  useInvestorEngagementMetrics,
  useInvestorReviews,
  averageRating,
  reviewToTestimonial,
} from '@/lib/investor/profile-signals';
import { deriveInvestorBadges } from '@/lib/investor/badges';
import { useCurrentTier } from '@/lib/state/subscription-store';
import { INVESTOR_TIERS, type InvestorTier } from '@/lib/subscription/types';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { useMutation } from '@tanstack/react-query';
import { handleError } from '@/lib/errors';
import { ReportContentModal } from '@/components/moderation/ReportContentModal';
import type { ReportTarget } from '@/lib/moderation/api';
import { openExternalUrl } from '@/lib/linking';
import { ActivityIndicator } from 'react-native';
import { PROFILE_PLANS_LINK, REFERRAL_LINK } from '@/lib/commerce/navigation';
import { COPY, planLabel } from '@/lib/commerce/copy';

// Format valuation for display
const formatValuation = (value: number | undefined): string => {
  if (!value) return '';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

/**
 * Colours for the real tiers.
 *
 * This used to be a map of `free | portfolio | full` — tier names that do not
 * exist anywhere in the subscription system (the real ones are
 * free | starter | standard | premium, see lib/subscription/types.ts). It was
 * read with a hardcoded `const currentTier = 'full'`, so every investor was
 * told they were on "Full Access · CHF 99/mo" no matter what they paid.
 * Name and price now come from INVESTOR_TIERS, and the tier from
 * useCurrentTier().
 */
const getTierColors = (colors: ReturnType<typeof useThemeColors>): Record<
  InvestorTier,
  { color: string; bg: string }
> => ({
  free: { color: colors.text.tertiary, bg: colors.light[300] },
  starter: { color: colors.primary[600], bg: colors.primary[50] },
  standard: { color: colors.primary[700], bg: colors.primary[50] },
  premium: { color: colors.success, bg: colors.traffic.greenLight },
});

// Edit button component
function EditButton({ onPress, colors }: { onPress: () => void; colors: any }) {
  return (
    <AnimatedPressable
      onPress={onPress}
      className="w-8 h-8 rounded-lg items-center justify-center"
      style={{ backgroundColor: colors.primary[50] }}
    >
      <Pencil size={14} color={colors.primary[600]} />
    </AnimatedPressable>
  );
}

// Section header with edit button
function SectionHeader({
  label,
  onEdit,
  colors,
}: {
  label: string;
  onEdit?: () => void;
  colors: any;
}) {
  return (
    <View className="flex-row items-center justify-between px-5 mb-3">
      <SectionDivider label={label} marginTop={8} marginBottom={0} />
      {onEdit && <EditButton onPress={onEdit} colors={colors} />}
    </View>
  );
}

export default function InvestorProfileScreen() {
  useTrackScreen('InvestorProfile');
  const router = useRouter();
  // `authUser` is deliberately distinct from the profile-store `user` below:
  // `investor_track_record` is keyed on auth.users(id), and the store's user
  // is not guaranteed to be that id.
  const { signOut, user: authUser, refreshProfile } = useAuth();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const TIER_COLORS = getTierColors(colors);

  // Store state
  const storeProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const user = useProfileStore((s) => s.user);
  const setUser = useProfileStore((s) => s.setUser);
  // Track-record count for the "Portfolio" stat.
  //
  // This used to read `useDealflowStore.portfolio.length` — a local store only
  // ever populated by `getMockPortfolio()` at demo login, so in production it
  // was always 0 and the tile silently fell through to the stale
  // `profile.portfolioCount` aggregate. That aggregate was the ONLY thing
  // onboarding persisted, which is how a profile could claim "12 investments"
  // and list none. Reads the real records now.
  const { data: trackRecord = [] } = useTrackRecord(authUser?.id);
  const portfolioCount = trackRecord.length;

  // Real engagement + reviews, replacing the mock-only `profile.engagement`,
  // `profile.ddActivity`, `profile.patterns` and `profile.reviews`. Every field
  // is null when unmeasured — the tiles below render nothing rather than a
  // fabricated figure. See lib/investor/profile-signals.ts.
  const { data: engagement } = useInvestorEngagementMetrics(authUser?.id);
  const { data: reviews = [] } = useInvestorReviews(authUser?.id);
  const reviewAverage = averageRating(reviews);

  const getInvestorCommitmentMetrics = useDealflowStore((s) => s.getInvestorCommitmentMetrics);
  const commitments = useDealflowStore((s) => s.commitments);
  const reset = useInverioStore((s) => s.reset);
  const { confirmDestructive, alert } = useAlert();

  // Get mock data (returns undefined in production mode, only used in dev/mock mode)
  const mockProfile = mode.isMock ? getMockEnhancedInvestorProfile() : undefined;
  const updateProfileMutation = useUpdateInvestorProfile();

  // LinkedIn enrichment — calls edge function and refreshes store
  const enrichMutation = useMutation({
    mutationFn: async () => {
      const linkedInUrl = storeProfile?.linkedInUrl;
      if (!linkedInUrl) throw new Error('No LinkedIn URL on profile');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL || '';
      const resp = await fetch(`${supabaseUrl}/functions/v1/enrich-linkedin-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ linkedin_url: linkedInUrl }),
      });

      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || 'Enrichment failed');
      // A NO-OP ARRIVES AS HTTP 200. The edge function returns
      // `{ error, skipped: true }` with status 200 for all three of its
      // give-up paths — no LINKDAPI_API_KEY, upstream fetch failed, no investor
      // row. `resp.ok` is true for every one of them, so the only check here
      // passed, onSuccess ran a pointless re-fetch, the spinner stopped, and
      // nothing changed with nothing said. Treat a skip as the failure it is.
      if (result?.skipped || !result?.success) {
        throw new Error(result?.error || 'LinkedIn refresh is unavailable right now');
      }
      return result;
    },
    onSuccess: async (result: { enriched_fields?: string[] }) => {
      // A SECOND, QUIETER NO-OP: enrichment only fills fields that are EMPTY,
      // but always stamps linkedin_verified/_at. So an already-complete profile
      // succeeds having changed nothing the user can see. Say so rather than
      // letting the spinner imply an update happened.
      const meaningful = (result?.enriched_fields ?? []).filter(
        (f) => f !== 'linkedin_verified' && f !== 'linkedin_verified_at',
      );
      if (meaningful.length === 0) {
        alert('Already up to date', 'LinkedIn had nothing new to add to your profile.');
      }
      // Re-fetch investor profile from DB to pick up enriched data
      if (!storeProfile?.id) return;
      const { data } = await supabase
        .from('investor_profiles')
        .select('*')
        .eq('id', storeProfile.id)
        .single();
      if (data) {
        const prevRoles = (data.previous_roles as any[] || []).map((r: any) => ({
          title: r.title, company: r.company, years: r.years,
        }));
        setInvestorProfile({
          ...storeProfile,
          bio: data.bio || data.headline || prevRoles.length
            ? {
                headline: data.headline || storeProfile.bio?.headline,
                bio: data.bio || storeProfile.bio?.bio,
                previousRoles: prevRoles.length > 0 ? prevRoles : storeProfile.bio?.previousRoles,
              }
            : storeProfile.bio,
          valueAdd: data.value_add
            ? { ...storeProfile.valueAdd, expertiseAreas: storeProfile.valueAdd?.expertiseAreas || [], description: data.value_add }
            : storeProfile.valueAdd,
          firm: data.firm || storeProfile.firm,
          location: data.location || storeProfile.location,
          yearsInvesting: data.years_investing || storeProfile.yearsInvesting,
          linkedInVerified: data.linkedin_verified || storeProfile.linkedInVerified,
          linkedInVerifiedAt: data.linkedin_verified_at || storeProfile.linkedInVerifiedAt,
        });
      }
    },
    onError: (err) => {
      // Was the raw global `alert(message)` — a browser API that does not
      // exist on native, so the one place this error could be reported was
      // the one place it never showed.
      const { message, isCancelled } = handleError(err, { context: 'InvestorProfile.enrichLinkedIn' });
      if (!isCancelled) alert("Couldn't refresh from LinkedIn", message);
    },
  });

  // Initialize profile from store or mock data
  const profile = useMemo(() => {
    if (storeProfile?.bio) return storeProfile; // Already has enhanced data
    // In production (no mock data), use store profile directly
    if (!mockProfile) return storeProfile;
    // In mock/demo mode, merge store with mock — real data takes priority
    return {
      ...mockProfile,
      ...storeProfile,
      bio: storeProfile?.bio || mockProfile.bio,
      thesis: storeProfile?.thesis || mockProfile.thesis,
      valueAdd: storeProfile?.valueAdd || mockProfile.valueAdd,
      engagement: storeProfile?.engagement || mockProfile.engagement,
      ddActivity: storeProfile?.ddActivity || mockProfile.ddActivity,
      patterns: storeProfile?.patterns || mockProfile.patterns,
      socialLinks: storeProfile?.socialLinks || mockProfile.socialLinks,
    };
  }, [storeProfile, mockProfile]);

  /** `thesis.sectors` is [] once a thesis exists, and [] is truthy — so the
   *  old `sectors || investmentFocus` expression stopped falling back the
   *  moment an investor saved a thesis without sectors. */
  const focusAreas = useMemo(
    () => (profile?.thesis?.sectors?.length ? profile.thesis.sectors : profile?.investmentFocus ?? []),
    [profile?.thesis?.sectors, profile?.investmentFocus],
  );
  /**
   * Badges are DERIVED, never stored. The field they replace had no column, no
   * mapper and no writer — its only source was a literal array in mock-data, so
   * demo mode advertised "50+ investments" and "94% response rate" about an
   * investor who had neither, and production showed nothing at all.
   *
   * Every input here is already loaded above, so this costs one pass over data
   * in hand. See lib/investor/badges.ts for what is deliberately NOT derivable.
   */
  const badges = useMemo(
    () =>
      deriveInvestorBadges({
        // `profile`, not `storeProfile`: in production the two are the same
        // object (the memo returns storeProfile when there is no mock), and in
        // mock mode this lets the demo investor's verified flag reach the
        // derivation like a real one would.
        linkedInVerified: profile?.linkedInVerified,
        linkedInVerifiedAt: profile?.linkedInVerifiedAt,
        trackRecord,
        engagement,
        reviews,
      }),
    [profile?.linkedInVerified, profile?.linkedInVerifiedAt, trackRecord, engagement, reviews],
  );

  // Edit modal state
  const [editModal, setEditModal] = useState<{
    visible: boolean;
    section: string;
    title: string;
    data: any;
  }>({ visible: false, section: '', title: '', data: {} });

  const [reportTarget, setReportTarget] = useState<{ type: ReportTarget; id: string } | null>(null);

  const openEditModal = useCallback((section: string, title: string, data: any) => {
    setEditModal({ visible: true, section, title, data });
  }, []);

  const closeEditModal = useCallback(() => {
    setEditModal((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleSaveEdit = useCallback(
    (data: any) => {
      track('profile_updated', { section: 'investor' });
      const currentProfile = profile || storeProfile;
      if (!currentProfile) return;

      switch (editModal.section) {
        case 'user':
          // `email` and `role` are no longer edited here — see
          // src/lib/investor/profile-edit-fields.ts for why `role` in
          // particular must never be written from the client.
          setUser({
            ...user,
            name: capitalizeWords(data.name || user?.name),
            email: user?.email || '',
          });
          break;

        case 'basic':
          setInvestorProfile({
            ...currentProfile,
            firm: data.firm,
            location: data.location,
            yearsInvesting: parseInt(data.yearsInvesting) || undefined,
          });
          break;

        case 'bio':
          setInvestorProfile({
            ...currentProfile,
            bio: {
              ...currentProfile.bio,
              headline: data.headline,
              bio: data.bio,
              // Mirror the writer's rule: only touch roles when the editor
              // actually supplied them, so an older call site cannot blank them.
              ...(Array.isArray(data.previousRoles)
                ? { previousRoles: data.previousRoles }
                : {}),
            },
          });
          break;

        case 'thesis':
          setInvestorProfile({
            ...currentProfile,
            investmentFocus: data.sectors?.filter(Boolean) || currentProfile.investmentFocus,
            stagePreference: data.stagePreference?.filter(Boolean) || currentProfile.stagePreference,
            thesis: {
              ...currentProfile.thesis,
              thesisStatement: data.thesisStatement,
              sweetSpot: data.sweetSpot,
              sectors: data.sectors?.filter(Boolean) || [],
              teamPreferences: data.teamPreferences?.filter(Boolean) || [],
              dealBreakers: data.dealBreakers?.filter(Boolean) || [],
            },
          });
          break;

        case 'ticketSize':
          setInvestorProfile({
            ...currentProfile,
            ticketSize: {
              min: parseInt(data.min) || currentProfile.ticketSize.min,
              max: parseInt(data.max) || currentProfile.ticketSize.max,
            },
          });
          break;

        case 'valueAdd':
          setInvestorProfile({
            ...currentProfile,
            valueAdd: {
              ...currentProfile.valueAdd,
              expertiseAreas: data.expertiseAreas?.filter(Boolean) || [],
              description: data.description,
              officeHours: data.officeHours,
              boardExperience: parseInt(data.boardExperience) || undefined,
            },
          });
          break;

        case 'social':
          setInvestorProfile({
            ...currentProfile,
            socialLinks: {
              twitter: data.twitter,
              website: data.website,
              calendly: data.calendly,
              substack: data.substack,
            },
          });
          break;
      }

      // ---------------------------------------------------------------
      // Persist.
      //
      // The store patch above is optimistic UI; THIS is the save. It used to
      // be a second switch that covered four of seven sections, so thesis and
      // social edits were never written at all and the store — persisted, and
      // cleared on sign-out — was the only copy. The section→column mapping
      // now lives in one tested place.
      //
      // Awaited and surfaced: supabase-js resolves with `{ error }` rather
      // than throwing, so a fire-and-forget `.mutate()` reports success no
      // matter what the server said (ARCH-06).
      // ---------------------------------------------------------------
      const section = editModal.section as InvestorEditSection;

      void (async () => {
        try {
          if (!isSupabaseConfigured()) return;

          if (section === 'user') {
            if (!authUser?.id) throw new Error('Not signed in.');
            const nameUpdate = buildInvestorNameUpdate(
              capitalizeWords(data.name || user?.name || '')
            );
            const { error: nameError } = await supabase
              .from('profiles')
              .update(nameUpdate)
              .eq('id', authUser.id);
            if (nameError) throw nameError;

            // `investor_profiles.name` is NOT NULL and is the name rendered on
            // the discover-investors card and the public submission link. If
            // only `profiles` were updated the two would disagree.
            if (currentProfile.id) {
              const display = [nameUpdate.first_name, nameUpdate.last_name]
                .filter(Boolean)
                .join(' ');
              const { error: mirrorError } = await supabase
                .from('investor_profiles')
                .update({ name: display })
                .eq('id', currentProfile.id);
              if (mirrorError) throw mirrorError;
            }
            await refreshProfile();
            return;
          }

          if (!currentProfile.id) {
            // No row to write to. Previously this branch just skipped the
            // save in silence.
            throw new Error(
              'Your investor profile has not been created yet. Finish profile setup first.'
            );
          }

          await updateProfileMutation.mutateAsync({
            investorId: currentProfile.id,
            updates: buildInvestorProfileDbUpdate(section, data),
          });
        } catch (err) {
          const { message, isCancelled } = handleError(err, {
            context: `InvestorProfile.handleSaveEdit.${section}`,
          });
          if (!isCancelled) alert("Couldn't save", message);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync is stable
    [
      editModal.section,
      profile,
      storeProfile,
      setInvestorProfile,
      setUser,
      user,
      authUser?.id,
      refreshProfile,
      alert,
    ]
  );

  const handleLogout = async () => {
    const confirmed = await confirmDestructive(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out'
    );
    if (!confirmed) return;
    try {
      // Single source of truth: revokes the Supabase session + resets all
      // user-scoped stores. Without this the session survives and the user is
      // bounced straight back into the app (the native sign-out bug, 2026-06-13).
      await signOut();
      reset(); // belt-and-suspenders store reset
    } catch (error) {
      handleError(error, { context: 'InvestorProfile.handleLogout' });
    } finally {
      router.replace('/login');
    }
  };

  const handleOpenLink = (url: string) => {
    openExternalUrl(url, 'InvestorProfile.openLink');
  };

  // The real tier. Was `'full'`, hardcoded, so a free investor was shown
  // "Full Access · CHF 99/mo".
  const currentTier = useCurrentTier() as InvestorTier;
  const tierConfig = INVESTOR_TIERS[currentTier] ?? INVESTOR_TIERS.free;

  const displayName = user?.name || 'Investor';
  // No 'Angel Investor' default. A headline is something the investor writes
  // about themselves; inventing one puts words in their mouth, and every
  // investor without a headline was being described identically to founders.
  const headline = profile?.bio?.headline?.trim() || null;

  /**
   * The stats row, derived rather than fixed.
   *
   * Each tile is included ONLY if it has a real value. `null` from the metrics
   * RPC means "not measured", which must render as nothing — an investor
   * nobody has messaged has no response rate, which is a different claim from
   * a 0% response rate.
   */
  const statTiles = useMemo(() => {
    const tiles: { key: string; icon: React.ReactNode; value: string; label: string }[] = [];

    // Real records, not the stale `portfolio_count` aggregate.
    if (portfolioCount > 0) {
      tiles.push({
        key: 'portfolio',
        icon: <Briefcase size={20} color={colors.primary[600]} />,
        value: String(portfolioCount),
        label: 'Portfolio',
      });
    }

    if (engagement?.responseRate != null) {
      tiles.push({
        key: 'response',
        icon: <Zap size={20} color={colors.warning} />,
        value: `${Math.round(engagement.responseRate)}%`,
        label: 'Response',
      });
    }

    if (engagement?.medianResponseHours != null) {
      const h = engagement.medianResponseHours;
      tiles.push({
        key: 'replyTime',
        icon: <Timer size={20} color={colors.primary[600]} />,
        value: h < 24 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`,
        label: 'Replies in',
      });
    }

    if (reviewAverage != null) {
      tiles.push({
        key: 'rating',
        icon: <Star size={20} color={colors.amber.main} fill={colors.amber.main} />,
        value: reviewAverage.toFixed(1),
        label: 'Rating',
      });
    }

    return tiles;
  }, [portfolioCount, engagement, reviewAverage, colors]);

  // The section renders only when at least one metric was actually measurable.
  const hasActivityMetrics =
    engagement != null &&
    (engagement.responseRate != null ||
      engagement.ddInProgress != null ||
      engagement.avgDecisionDays != null ||
      engagement.dataRoomRequests30d != null);

  const commitmentMetrics = profile ? getInvestorCommitmentMetrics(profile.id) : null;
  const hasReliableBadge = commitmentMetrics &&
    commitmentMetrics.softToHardRatio >= 80 &&
    (commitmentMetrics.totalSoftCommits + commitmentMetrics.totalHardCommits) >= 3;

  // In production, if profile hasn't been set up yet, show minimal state
  // with submission link settings (users should manage links without full profile)
  if (!profile) {
    return (
      <AppLayout title="Profile">
        <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
          {/* BOTH branches carry the return contract. Profile left the tab bar
             for the top-right avatar, so it is a secondary page now — and the
             avatar can be pressed from anywhere, which is precisely why the
             fallback matters. The startup counterpart was migrated in the same
             pass; leaving this one behind is what made the two account
             destinations behave differently. */}
          <ScreenFrame
            title="Profile"
            back={{ fallback: '/(investor)/investor-dashboard', label: 'Home' }}
          >
            <ScrollView className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 100 }}>
              <View className="items-center px-6 pt-8 pb-4">
                {user?.avatarUrl ? (
                  <View className="w-20 h-20 rounded-full overflow-hidden mb-3">
                    <Animated.Image
                      entering={FadeIn.duration(400)}
                      source={{ uri: user.avatarUrl }}
                      style={{ width: 80, height: 80 }}
                    />
                  </View>
                ) : (
                  <View className="w-20 h-20 rounded-full items-center justify-center mb-3" style={{ backgroundColor: colors.primary[100] }}>
                    <User size={36} color={colors.primary[600]} />
                  </View>
                )}
                <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                  {displayName}
                </Text>
                {user?.email && (
                  <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1">
                    {user.email}
                  </Text>
                )}
              </View>

              {/* Submission Link — always accessible */}
              <View className="px-5 mb-4">
                <SubmissionLinkSettings />
              </View>

              {/* Profile completion prompt */}
              <View className="mx-5 p-5 rounded-2xl" style={{ backgroundColor: colors.surface.primary }}>
                <Text style={{ color: colors.text.primary }} className="text-base font-semibold mb-2">
                  Complete your profile
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-sm mb-4">
                  Add your investment thesis, focus areas, and bio to attract better-matched startups.
                </Text>
                <AnimatedPressable
                  onPress={() => router.push('/(onboarding)/investor-identity')}
                  className="py-3 rounded-xl items-center"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Text className="text-white font-semibold">Set Up Profile</Text>
                </AnimatedPressable>
              </View>

              {/* Sign out */}
              <AnimatedPressable onPress={handleLogout} className="mx-5 mt-4 py-3 items-center">
                <Text style={{ color: colors.error }} className="font-medium">Sign Out</Text>
              </AnimatedPressable>
            </ScrollView>
          </ScreenFrame>
        </View>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Profile">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* The title/Edit row used to sit INSIDE this ScrollView with no back
           control, so it scrolled away and there was no return at all. Edit is a
           contextual action for this page, which is what the trailing slot is
           for; it also gains the 44pt target and the accessible name it never
           had. */}
        <ScreenFrame
          title="Profile"
          back={{ fallback: '/(investor)/investor-dashboard', label: 'Home' }}
          actions={
            <AnimatedPressable
              onPress={() => openEditModal('user', 'Edit Personal Info', { name: displayName })}
              accessibilityRole="button"
              accessibilityLabel="Edit personal information"
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: colors.light[300],
              }}
            >
              <Edit3 size={18} color={colors.text.secondary} />
            </AnimatedPressable>
          }
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>

            {/* Profile Hero Card */}
            <Animated.View entering={FadeInUp.delay(150).duration(500)} className="mx-5 mb-4">
              {/* TWO LAYERS, DELIBERATELY. The teal accent is absolutely
                 positioned at the card's top edge, and the card is `rounded-3xl`
                 — so without clipping it drew as a full-width bar floating above
                 the card's rounded corners instead of sitting on them. That is
                 the `<layout_containment>` rule in CLAUDE.md: a rounded card
                 must clip its children.

                 The clip cannot go on the shadow-casting view. On iOS
                 `overflow: 'hidden'` sets `masksToBounds`, which clips the
                 SHADOW too and would flatten the card. So the outer view keeps
                 the shadow and the background, and an inner view does the
                 clipping. */}
              <View
                className="rounded-3xl"
                style={{
                  backgroundColor: colors.surface.primary,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.1,
                  shadowRadius: 16,
                  elevation: 4,
                }}
              >
              <View className="rounded-3xl overflow-hidden p-6">
                <View className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: colors.primary[500] }} />

                {/* Top row: avatar + identity */}
                <View className="flex-row items-start">
                  <AnimatedPressable
                    onPress={() => openEditModal('user', 'Edit Personal Info', { name: displayName })}
                    className="relative mr-4"
                  >
                    {user?.avatarUrl ? (
                      <View
                        className="w-20 h-20 rounded-2xl overflow-hidden"
                        style={{ borderWidth: 2, borderColor: colors.primary[500] }}
                      >
                        <Image
                          source={{ uri: user.avatarUrl }}
                          style={{ width: '100%', height: '100%' }}
                          resizeMode="cover"
                        />
                      </View>
                    ) : (
                      <View className="w-20 h-20 rounded-2xl items-center justify-center" style={{ backgroundColor: colors.primary[100] }}>
                        <Text style={{ color: colors.primary[600] }} className="text-3xl font-bold">
                          {displayName.split(' ').map((n) => n[0]).join('')}
                        </Text>
                      </View>
                    )}
                    <View
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full items-center justify-center border-2"
                      style={{ backgroundColor: colors.primary[600], borderColor: colors.surface.primary }}
                    >
                      <Pencil size={12} color="white" />
                    </View>
                  </AnimatedPressable>

                  <View className="flex-1">
                    <View className="flex-row items-center justify-between">
                      <Text style={{ color: colors.text.primary }} className="text-xl font-bold" numberOfLines={1}>
                        {displayName}
                      </Text>
                      <View className="flex-row items-center gap-2">
                        {profile.linkedInUrl && (
                          <AnimatedPressable
                            onPress={() => enrichMutation.mutate()}
                            disabled={enrichMutation.isPending}
                            className="w-8 h-8 rounded-lg items-center justify-center"
                            style={{ backgroundColor: '#0A66C2' + '18' }}
                          >
                            {enrichMutation.isPending ? (
                              <ActivityIndicator size="small" color="#0A66C2" />
                            ) : (
                              <Linkedin size={14} color="#0A66C2" />
                            )}
                          </AnimatedPressable>
                        )}
                        <EditButton
                          onPress={() => openEditModal('basic', 'Edit Firm Info', {
                            firm: profile.firm,
                            location: profile.location,
                            yearsInvesting: profile.yearsInvesting?.toString(),
                          })}
                          colors={colors}
                        />
                      </View>
                    </View>
                    {headline && (
                      <Text style={{ color: colors.text.secondary }} className="text-sm mt-0.5" numberOfLines={2}>
                        {headline}
                      </Text>
                    )}
                    {profile.firm && (
                      <View className="flex-row items-center gap-1.5 mt-1">
                        <Building2 size={12} color={colors.text.tertiary} />
                        <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                          {profile.firm}
                        </Text>
                      </View>
                    )}
                    {profile.location && (
                      <View className="flex-row items-center gap-1.5 mt-0.5">
                        <MapPin size={12} color={colors.text.tertiary} />
                        <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                          {profile.location}
                        </Text>
                      </View>
                    )}

                    <View className="flex-row flex-wrap gap-2 mt-2.5">
                      <View className="flex-row items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: TIER_COLORS[currentTier].bg }}>
                        <Crown size={12} color={TIER_COLORS[currentTier].color} />
                        <Text style={{ color: TIER_COLORS[currentTier].color }} className="text-xs font-semibold">
                          {tierConfig.name}
                        </Text>
                      </View>
                      {profile.linkedInVerified && (
                        <View className="flex-row items-center gap-1 px-2.5 py-1 rounded-full" style={{ backgroundColor: colors.traffic.greenLight }}>
                          <CheckCircle size={12} color={colors.traffic.green} />
                          <Text style={{ color: colors.traffic.green }} className="text-xs font-semibold">Verified</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </View>

                {/* Bio / About.
                    `INVESTOR_EDITOR_FIELDS.bio`, the modal's `case 'bio'` and the
                    `bio` branch of `buildInvestorProfileDbUpdate` all existed
                    already — nothing on this screen ever opened them, so
                    `headline` and `bio` were settable only by onboarding or by
                    LinkedIn enrichment. That is why the tagline and the About
                    text were blank for most real investors. */}
                <View className="mt-4">
                  <View className="flex-row items-center justify-between">
                    <View className="flex-1">
                      <SectionDivider label="About" marginTop={0} marginBottom={8} />
                    </View>
                    <EditButton
                      onPress={() => openEditModal('bio', 'Edit Bio', {
                        headline: profile.bio?.headline,
                        bio: profile.bio?.bio,
                        previousRoles: profile.bio?.previousRoles || [],
                      })}
                      colors={colors}
                    />
                  </View>
                  {profile.bio?.bio ? (
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                      {profile.bio.bio}
                    </Text>
                  ) : (
                    <Text style={{ color: colors.text.tertiary }} className="text-sm italic">
                      Add a bio to help founders understand your background
                    </Text>
                  )}
                </View>

                {/* Previous Roles / Experience */}
                {profile.bio?.previousRoles && profile.bio.previousRoles.length > 0 && (
                  <View className="mt-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium uppercase tracking-wider mb-3">
                      Experience
                    </Text>
                    <View className="gap-2.5">
                      {profile.bio.previousRoles.map((role, index) => (
                        <View key={`role-${index}`} className="flex-row items-center gap-3">
                          <View className="w-8 h-8 rounded-lg items-center justify-center" style={{ backgroundColor: colors.light[300] }}>
                            <Building2 size={14} color={colors.text.secondary} />
                          </View>
                          <View className="flex-1">
                            <Text style={{ color: colors.text.primary }} className="font-medium text-sm" numberOfLines={1}>{role.title}</Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                              {role.company}{role.years ? ` · ${role.years}` : ''}
                            </Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {/* Footer stats.
                    Dropped: a "Member since 2024" fallback (a literal year, shown
                    to everyone who had not set yearsInvesting) and an "Active now"
                    dot that was rendered unconditionally — it tracked nothing, so
                    it was green for a dormant account as readily as a live one.
                    Presence signals are a promise to founders; we do not make one
                    we cannot keep. */}
                {profile.yearsInvesting ? (
                  <View className="flex-row items-center gap-2 mt-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>
                    <Calendar size={14} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm">
                      {profile.yearsInvesting} years investing
                    </Text>
                  </View>
                ) : null}
              </View>
              </View>
            </Animated.View>

            {/* Badges Section */}
            {badges.length > 0 && (
              <Animated.View entering={FadeInUp.delay(200).duration(500)} className="px-5 mb-4">
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
                  {badges.slice(0, 6).map((badge, index) => (
                    <Badge key={`${badge.type}-${index}`} type={badge.type} size="md" detail={badge.detail} />
                  ))}
                </ScrollView>
              </Animated.View>
            )}

            {/* Quick Stats Row.
                Built from data that EXISTS. Previously four fixed tiles, three of
                which could not tell you the truth:
                  · Response fell back to a hardcoded '94%' for every investor.
                  · Avg Check fell back to ticketSize.min — a stated preference
                    relabelled as observed behaviour.
                  · Portfolio fell through to the stale portfolio_count aggregate.
                A tile with nothing behind it is now simply absent, and the row
                hides entirely when nothing is measurable. */}
            {statTiles.length > 0 && (
              <Animated.View entering={FadeInUp.delay(250).duration(500)} className="px-5 mb-4">
                <View className="flex-row flex-wrap gap-3">
                  {statTiles.map((tile) => (
                    <View
                      key={tile.key}
                      className="flex-1 rounded-2xl p-3 items-center"
                      style={{ backgroundColor: colors.surface.primary, minWidth: 76 }}
                    >
                      {tile.icon}
                      <Text
                        style={{ color: colors.text.primary }}
                        className="text-lg font-bold mt-1"
                        numberOfLines={1}
                      >
                        {tile.value}
                      </Text>
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                        numberOfLines={1}
                      >
                        {tile.label}
                      </Text>
                    </View>
                  ))}
                </View>
              </Animated.View>
            )}

            {/* Investment Thesis Section */}
            <View className="flex-row items-center justify-between px-5">
              <View className="flex-1">
                <SectionDivider label="Investment Thesis" marginTop={8} marginBottom={12} />
              </View>
              <EditButton
                onPress={() => openEditModal('thesis', 'Edit Thesis', {
                  thesisStatement: profile.thesis?.thesisStatement,
                  sweetSpot: profile.thesis?.sweetSpot,
                  sectors: profile.thesis?.sectors || profile.investmentFocus,
                  stagePreference: profile.stagePreference,
                  teamPreferences: profile.thesis?.teamPreferences || [],
                  dealBreakers: profile.thesis?.dealBreakers || [],
                })}
                colors={colors}
              />
            </View>

            <Animated.View entering={FadeInUp.delay(350).duration(500)} className="px-5 mb-4">
              <View className="rounded-2xl p-5" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}>
                {profile.thesis?.thesisStatement && (
                  <View className="mb-5">
                    <View className="flex-row items-center gap-2 mb-2">
                      <Quote size={16} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">Philosophy</Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                      {profile.thesis.thesisStatement}
                    </Text>
                  </View>
                )}

                {profile.thesis?.sweetSpot && (
                  <View className="rounded-xl p-4 mb-5" style={{ backgroundColor: colors.primary[50] }}>
                    <View className="flex-row items-center gap-2 mb-1">
                      <Target size={14} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[700] }} className="text-xs font-medium uppercase">Sweet Spot</Text>
                    </View>
                    <Text style={{ color: colors.primary[800] }} className="font-semibold">{profile.thesis.sweetSpot}</Text>
                  </View>
                )}

                {/* Guarded like Preferred Founders and Deal Breakers below: the
                    heading used to render with nothing underneath it.
                    `sectors` is [] (truthy) once a thesis exists, so the `||`
                    fallback to investmentFocus could never fire — hence the
                    explicit length check rather than reusing that expression. */}
                {focusAreas.length > 0 && (
                  <View className="mb-4">
                    <View className="flex-row items-center gap-2 mb-3">
                      <Target size={16} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">Focus Areas</Text>
                    </View>
                    <View className="flex-row flex-wrap gap-2">
                      {focusAreas.map((focus, index) => (
                        <View key={index} className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.primary[50] }}>
                          <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">{focus}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {profile.stagePreference.length > 0 && (
                  <View className="mb-4">
                    <View className="flex-row items-center gap-2 mb-3">
                      <TrendingUp size={16} color={colors.success} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">Stage Preference</Text>
                    </View>
                    <View className="flex-row flex-wrap gap-2">
                      {profile.stagePreference.map((stage, index) => (
                        <View key={index} className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.traffic.greenLight }}>
                          <Text style={{ color: colors.success }} className="text-sm font-medium capitalize">{stage.replace('-', ' ')}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {profile.thesis?.teamPreferences && profile.thesis.teamPreferences.length > 0 && (
                  <View className="mb-4">
                    <View className="flex-row items-center gap-2 mb-3">
                      <Users size={16} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">Preferred Founders</Text>
                    </View>
                    <View className="flex-row flex-wrap gap-2">
                      {profile.thesis.teamPreferences.map((pref, index) => (
                        <View key={index} className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.light[300] }}>
                          <Text style={{ color: colors.text.secondary }} className="text-sm">{pref}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                {profile.thesis?.dealBreakers && profile.thesis.dealBreakers.length > 0 && (
                  <View className="mb-4">
                    <View className="flex-row items-center gap-2 mb-3">
                      <XCircle size={16} color={colors.error} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">Deal Breakers</Text>
                    </View>
                    <View className="flex-row flex-wrap gap-2">
                      {profile.thesis.dealBreakers.map((breaker, index) => (
                        <View key={index} className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.red.light }}>
                          <Text style={{ color: colors.error }} className="text-sm">{breaker}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )}

                <View className="flex-row gap-4 pt-4 border-t" style={{ borderColor: colors.border.light }}>
                  <AnimatedPressable
                    onPress={() => openEditModal('ticketSize', 'Edit Check Size', {
                      // Blank, not "0" — prefilling a zero invites saving one.
                      min: profile.ticketSize.min ? profile.ticketSize.min.toString() : '',
                      max: profile.ticketSize.max ? profile.ticketSize.max.toString() : '',
                    })}
                    className="flex-1"
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <DollarSign size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Check Size</Text>
                      <Pencil size={10} color={colors.primary[600]} />
                    </View>
                    <Text
                      style={{ color: colors.text.primary }}
                      className="font-semibold"
                      numberOfLines={1}
                    >
                      {/* The mapper turns NULL columns into 0, so an investor who
                          never stated a check size rendered a literal "$0 - $0" —
                          a number they never gave, shown to founders as if they
                          had. An unset range says so. */}
                      {profile.ticketSize.min || profile.ticketSize.max
                        ? `${formatCurrency(profile.ticketSize.min)} - ${formatCurrency(profile.ticketSize.max)}`
                        : 'Not set'}
                    </Text>
                  </AnimatedPressable>
                  <View className="flex-1 min-w-0">
                    <View className="flex-row items-center gap-2 mb-2">
                      <Globe size={14} color={colors.text.tertiary} />
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                        numberOfLines={1}
                      >
                        Geography
                      </Text>
                    </View>
                    {/* A location is free text — "Zurich" fits, "Zurich, Switzerland
                        and the wider DACH region" does not. */}
                    <Text
                      style={{ color: colors.text.primary }}
                      className="font-semibold"
                      numberOfLines={1}
                    >
                      {/* WAS `profile.location` — the investor's OWN city, under a
                          label that promises where they INVEST. Different claims.
                          `geographies` is collected at onboarding (required
                          there), persisted, and already loaded by the mapper;
                          nothing on this screen had ever read it. */}
                      {profile.geographies?.length ? profile.geographies.join(', ') : 'Not set'}
                    </Text>
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* Value-Add Section — always visible */}
            <View className="flex-row items-center justify-between px-5">
              <View className="flex-1">
                <SectionDivider label="Value-Add" marginTop={8} marginBottom={12} />
              </View>
              <EditButton
                onPress={() => openEditModal('valueAdd', 'Edit Value-Add', {
                  expertiseAreas: profile.valueAdd?.expertiseAreas || [],
                  description: profile.valueAdd?.description,
                  officeHours: profile.valueAdd?.officeHours,
                  boardExperience: profile.valueAdd?.boardExperience?.toString(),
                })}
                colors={colors}
              />
            </View>

            <Animated.View entering={FadeInUp.delay(400).duration(500)} className="px-5 mb-4">
              {profile.valueAdd?.description || (profile.valueAdd?.expertiseAreas && profile.valueAdd.expertiseAreas.length > 0) ? (
                <View className="rounded-2xl p-5" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}>
                  {profile.valueAdd!.expertiseAreas && profile.valueAdd!.expertiseAreas.length > 0 && (
                    <View className="mb-4">
                      <View className="flex-row items-center gap-2 mb-3">
                        <Sparkles size={16} color={colors.primary[600]} />
                        <Text style={{ color: colors.text.primary }} className="font-semibold">Expertise</Text>
                      </View>
                      <View className="flex-row flex-wrap gap-2">
                        {profile.valueAdd!.expertiseAreas.map((area, index) => (
                          <View key={`expertise-${index}`} className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.amber.light }}>
                            <Text style={{ color: colors.amber.main }} className="text-sm font-medium">{area}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                  {profile.valueAdd!.description && (
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-5 mb-4">
                      {profile.valueAdd!.description}
                    </Text>
                  )}
                  <View className="flex-row gap-3">
                    {profile.valueAdd!.officeHours && (
                      <View className="flex-1 rounded-xl p-3 items-center" style={{ backgroundColor: colors.traffic.greenLight }}>
                        <Clock size={18} color={colors.success} />
                        <Text style={{ color: colors.success }} className="text-xs font-medium mt-1">Office Hours</Text>
                      </View>
                    )}
                    {profile.valueAdd!.boardExperience && profile.valueAdd!.boardExperience > 0 && (
                      <View className="flex-1 rounded-xl p-3 items-center" style={{ backgroundColor: colors.primary[50] }}>
                        <Users size={18} color={colors.primary[600]} />
                        <Text style={{ color: colors.primary[600] }} className="font-bold mt-1">{profile.valueAdd!.boardExperience}</Text>
                        <Text style={{ color: colors.primary[600] }} className="text-xs">Board Seats</Text>
                      </View>
                    )}
                  </View>
                </View>
              ) : (
                <View className="rounded-2xl p-5 items-center" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light, borderStyle: 'dashed' }}>
                  <HandHelping size={24} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.tertiary }} className="text-sm mt-2 text-center">
                    Describe how you help your portfolio companies
                  </Text>
                </View>
              )}
            </Animated.View>

            {/* Submissions Section */}
            <View className="px-5">
              <SectionDivider label="Submissions" icon={Inbox} marginTop={8} marginBottom={12} />
            </View>

            <Animated.View entering={FadeInUp.delay(425).duration(500)} className="px-5 mb-4">
              <SubmissionLinkSettings />
            </Animated.View>

            {/* Activity Metrics — server-derived.
                Was `profile.engagement` / `profile.ddActivity` / `profile.patterns`,
                three mock-only objects with no table behind them, so this whole
                section existed in demo mode and vanished in production.
                "Follow-On" and "Intros Made" are gone rather than reimplemented:
                nothing the product records can derive them, and a plausible
                number with no source is the thing being removed here. */}
            {hasActivityMetrics && (
              <>
                <View className="px-5">
                  <SectionDivider label="Activity Metrics" marginTop={8} marginBottom={12} />
                </View>

                <Animated.View entering={FadeInUp.delay(450).duration(500)} className="px-5 mb-4">
                  <View className="flex-row flex-wrap gap-3">
                    {engagement?.responseRate != null && (
                      <View className="flex-1 rounded-2xl p-4" style={{ backgroundColor: colors.surface.primary, minWidth: 140 }}>
                        <View className="flex-row items-center gap-2 mb-2">
                          <Zap size={16} color={colors.success} />
                          <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>Response Rate</Text>
                        </View>
                        <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                          {Math.round(engagement.responseRate)}%
                        </Text>
                        {/* The denominator, so "100%" off a single thread reads
                            for what it is. */}
                        {engagement.eligibleThreadCount != null && (
                          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1" numberOfLines={1}>
                            of {engagement.eligibleThreadCount} thread
                            {engagement.eligibleThreadCount === 1 ? '' : 's'}
                          </Text>
                        )}
                      </View>
                    )}

                    {engagement?.ddInProgress != null && (
                      <View className="flex-1 rounded-2xl p-4" style={{ backgroundColor: colors.surface.primary, minWidth: 140 }}>
                        <View className="flex-row items-center gap-2 mb-2">
                          <FileSearch size={16} color={colors.primary[600]} />
                          <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>Active DD</Text>
                        </View>
                        <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                          {engagement.ddInProgress}
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-1" numberOfLines={1}>In progress</Text>
                      </View>
                    )}

                    {engagement?.avgDecisionDays != null && (
                      <View className="flex-1 rounded-2xl p-4 items-center" style={{ backgroundColor: colors.surface.primary, minWidth: 110 }}>
                        <Timer size={20} color={colors.warning} />
                        <Text style={{ color: colors.text.primary }} className="text-lg font-bold mt-2">
                          {Math.round(engagement.avgDecisionDays)}d
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>Avg Decision</Text>
                      </View>
                    )}

                    {engagement?.dataRoomRequests30d != null && (
                      <View className="flex-1 rounded-2xl p-4 items-center" style={{ backgroundColor: colors.surface.primary, minWidth: 110 }}>
                        <Inbox size={20} color={colors.primary[600]} />
                        <Text style={{ color: colors.text.primary }} className="text-lg font-bold mt-2">
                          {engagement.dataRoomRequests30d}
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>Requests / 30d</Text>
                      </View>
                    )}
                  </View>
                </Animated.View>
              </>
            )}

            {/* Commitment Track Record */}
            {commitmentMetrics && (commitmentMetrics.totalSoftCommits + commitmentMetrics.totalHardCommits) > 0 && (
              <>
                <View className="px-5">
                  <SectionDivider label="Commitment Track Record" icon={ShieldCheck} marginTop={8} marginBottom={12} />
                </View>

                <Animated.View entering={FadeInUp.delay(475).duration(500)} className="px-5 mb-4">
                  <View className="p-4 rounded-2xl" style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}>
                    <View className="flex-row items-center justify-between mb-3">
                      <Text className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                        Reliability Score
                      </Text>
                      {hasReliableBadge && (
                        <View className="flex-row items-center gap-1.5 px-2.5 py-1 rounded-full"
                          style={{ backgroundColor: `${colors.success}15` }}>
                          <ShieldCheck size={12} color={colors.success} />
                          <Text className="text-[10px] font-bold" style={{ color: colors.success }}>
                            Reliable Committer
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Circular progress indicator */}
                    <View className="items-center mb-4">
                      <View className="w-20 h-20 rounded-full items-center justify-center"
                        style={{
                          borderWidth: 4,
                          borderColor: colors.border.light,
                        }}>
                        <View className="absolute w-20 h-20 rounded-full overflow-hidden"
                          style={{
                            borderWidth: 4,
                            borderColor: colors.primary[500],
                            borderTopColor: commitmentMetrics.softToHardRatio >= 25 ? colors.primary[500] : colors.border.light,
                            borderRightColor: commitmentMetrics.softToHardRatio >= 50 ? colors.primary[500] : colors.border.light,
                            borderBottomColor: commitmentMetrics.softToHardRatio >= 75 ? colors.primary[500] : colors.border.light,
                            borderLeftColor: commitmentMetrics.softToHardRatio >= 100 ? colors.primary[500] : colors.border.light,
                          }} />
                        <Text className="text-lg font-bold" style={{ color: colors.text.primary }}>
                          {Math.round(commitmentMetrics.softToHardRatio)}%
                        </Text>
                        <Text className="text-[9px]" style={{ color: colors.text.tertiary }}>
                          conversion
                        </Text>
                      </View>
                    </View>

                    {/* Stats row */}
                    <View className="flex-row gap-2">
                      <View className="flex-1 items-center p-2 rounded-xl" style={{ backgroundColor: `${colors.primary[500]}08` }}>
                        <Text className="text-lg font-bold" style={{ color: colors.text.primary }}>
                          {commitmentMetrics.totalHardCommits}
                        </Text>
                        <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>Hard Commits</Text>
                      </View>
                      <View className="flex-1 items-center p-2 rounded-xl" style={{ backgroundColor: `${colors.primary[500]}08` }}>
                        <Text className="text-lg font-bold" style={{ color: colors.text.primary }}>
                          {formatCurrency(commitmentMetrics.averageCommitAmount)}
                        </Text>
                        <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>Avg Check</Text>
                      </View>
                      <View className="flex-1 items-center p-2 rounded-xl" style={{ backgroundColor: commitmentMetrics.totalWithdrawn > 0 ? `${colors.warning}10` : `${colors.primary[500]}08` }}>
                        <Text className="text-lg font-bold" style={{ color: commitmentMetrics.totalWithdrawn > 0 ? colors.warning : colors.text.primary }}>
                          {commitmentMetrics.totalWithdrawn}
                        </Text>
                        <Text className="text-[10px]" style={{ color: colors.text.tertiary }}>Withdrawn</Text>
                      </View>
                    </View>
                  </View>
                </Animated.View>
              </>
            )}

            {/* Portfolio Section */}
            <View className="px-5">
              <SectionDivider label="Portfolio" marginTop={8} marginBottom={12} />
            </View>

            <Animated.View entering={FadeInUp.delay(500).duration(500)} className="px-5 mb-4">
              <View className="rounded-2xl p-4" style={{ backgroundColor: colors.surface.primary }}>
                <View className="flex-row items-center justify-between mb-4">
                  <Text style={{ color: colors.text.primary }} className="font-semibold">Recent Investments</Text>
                  <Pressable onPress={() => router.push('/(onboarding)/investor-portfolio')} className="flex-row items-center gap-1">
                    <Plus size={14} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">Add</Text>
                  </Pressable>
                </View>

                {/* READS THE REAL TABLE. This used to render
                    `profile.investmentHistory`, a device-local zustand array —
                    so the Portfolio stat tile above (which already counts real
                    `investor_track_record` rows) could say "3" while this list
                    said "No investment history added yet": two halves of one
                    card disagreeing. `portfolio.tsx` made exactly this migration
                    already; see its note. */}
                {trackRecord.length > 0 ? (
                  <View className="gap-3">
                    {trackRecord.slice(0, 3).map((investment, index) => (
                      <View
                        key={investment.id}
                        className={`flex-row items-center gap-3 ${index < Math.min(trackRecord.length - 1, 2) ? 'pb-3 border-b' : ''}`}
                        style={{ borderColor: colors.border.light }}
                      >
                        <View
                          className="w-10 h-10 rounded-xl items-center justify-center"
                          style={{ backgroundColor: investment.investorRole === 'board-member' ? colors.amber.light : colors.primary[50] }}
                        >
                          {investment.investorRole === 'board-member' ? (
                            <Crown size={18} color={colors.amber.main} />
                          ) : (
                            <Building2 size={18} color={colors.primary[600]} />
                          )}
                        </View>
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="font-medium">{investment.companyName}</Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs">
                            {investment.stage} · {investment.date.split('-')[0]}
                            {investment.investmentAmount && ` · ${formatValuation(investment.investmentAmount)}`}
                          </Text>
                        </View>
                        {/* No status chip. `status` is a HOLDINGS concept that
                            lives in `portfolio_companies`, deliberately kept out
                            of the track record (see
                            `trackRecordRowToInvestmentRecord`) — it was only ever
                            populated by the device-local store this list no
                            longer reads. */}
                      </View>
                    ))}
                    {trackRecord.length > 3 && (
                      <Pressable onPress={() => router.push('/(investor)/portfolio')} className="pt-2">
                        <Text style={{ color: colors.primary[600] }} className="text-sm font-medium text-center">
                          View all {trackRecord.length} investments →
                        </Text>
                      </Pressable>
                    )}
                  </View>
                ) : (
                  <View className="items-center py-6">
                    <Briefcase size={32} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm mt-3 text-center">
                      No investment history added yet
                    </Text>
                    <Pressable
                      onPress={() => router.push('/(onboarding)/investor-portfolio')}
                      className="mt-4 px-5 py-2.5 rounded-xl"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <Text className="text-white font-semibold text-sm">Add Investment History</Text>
                    </Pressable>
                  </View>
                )}
              </View>
            </Animated.View>

            {/* Founder Reviews — real rows from investor_founder_reviews.
                Was `profile.reviews`, mock-only. Reads through
                get_investor_reviews(), which returns published rows only and
                masks the author when they asked to be anonymous. */}
            {reviews.length > 0 && reviewAverage != null && (
              <>
                <View className="px-5">
                  <SectionDivider label="Founder Reviews" marginTop={8} marginBottom={12} />
                </View>

                <Animated.View entering={FadeInUp.delay(550).duration(500)} className="px-5 mb-4">
                  <View className="rounded-2xl p-5" style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}>
                    <View className="flex-row items-center justify-between mb-4">
                      <View>
                        <View className="flex-row items-center gap-2">
                          <Text style={{ color: colors.text.primary }} className="text-3xl font-bold">
                            {reviewAverage.toFixed(1)}
                          </Text>
                          <StarRating rating={reviewAverage} size="md" />
                        </View>
                        <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1" numberOfLines={1}>
                          Based on {reviews.length} founder review{reviews.length === 1 ? '' : 's'}
                        </Text>
                      </View>
                    </View>

                    <View className="gap-3">
                      {reviews.slice(0, 2).map((review) => (
                        <TestimonialCard
                          key={review.id}
                          testimonial={reviewToTestimonial(review)}
                          compact
                          onReport={() => setReportTarget({ type: 'review', id: review.id })}
                        />
                      ))}
                    </View>

                    {reviews.length > 2 && (
                      <View className="mt-4">
                        <Text style={{ color: colors.text.tertiary }} className="text-sm text-center">
                          {/* Not a link: there is no all-reviews screen yet, and
                              the previous Pressable had no onPress — a control
                              that looks tappable and does nothing. */}
                          + {reviews.length - 2} more
                        </Text>
                      </View>
                    )}
                  </View>
                </Animated.View>
              </>
            )}

            {/* Social Links Section */}
            {profile.socialLinks && (
              <>
                <View className="flex-row items-center justify-between px-5">
                  <View className="flex-1">
                    <SectionDivider label="Connect" marginTop={8} marginBottom={12} />
                  </View>
                  <EditButton
                    onPress={() => openEditModal('social', 'Edit Social Links', {
                      twitter: profile.socialLinks?.twitter,
                      website: profile.socialLinks?.website,
                      calendly: profile.socialLinks?.calendly,
                      substack: profile.socialLinks?.substack,
                    })}
                    colors={colors}
                  />
                </View>

                <Animated.View entering={FadeInUp.delay(600).duration(500)} className="px-5 mb-4">
                  <View className="flex-row gap-3">
                    {profile.socialLinks.twitter && (
                      <Pressable
                        onPress={() => handleOpenLink(`twitter.com/${profile.socialLinks!.twitter}`)}
                        className="flex-1 rounded-xl p-4 items-center"
                        style={{ backgroundColor: '#E7F5FE' }}
                      >
                        <Twitter size={20} color="#1DA1F2" />
                        <Text style={{ color: '#1DA1F2' }} className="text-xs font-medium mt-2">
                          {profile.socialLinks.twitter}
                        </Text>
                      </Pressable>
                    )}
                    {profile.socialLinks.website && (
                      <Pressable
                        onPress={() => handleOpenLink(profile.socialLinks!.website!)}
                        className="flex-1 rounded-xl p-4 items-center"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Globe size={20} color={colors.primary[600]} />
                        <Text style={{ color: colors.primary[600] }} className="text-xs font-medium mt-2">Website</Text>
                      </Pressable>
                    )}
                    {profile.socialLinks.calendly && (
                      <Pressable
                        onPress={() => handleOpenLink(profile.socialLinks!.calendly!)}
                        className="flex-1 rounded-xl p-4 items-center"
                        style={{ backgroundColor: colors.traffic.greenLight }}
                      >
                        <Calendar size={20} color={colors.success} />
                        <Text style={{ color: colors.success }} className="text-xs font-medium mt-2">Schedule</Text>
                      </Pressable>
                    )}
                    {profile.socialLinks.substack && (
                      <Pressable
                        onPress={() => handleOpenLink(profile.socialLinks!.substack!)}
                        className="flex-1 rounded-xl p-4 items-center"
                        style={{ backgroundColor: colors.amber.light }}
                      >
                        <BookOpen size={20} color={colors.amber.main} />
                        <Text style={{ color: colors.amber.main }} className="text-xs font-medium mt-2">Newsletter</Text>
                      </Pressable>
                    )}
                  </View>
                </Animated.View>
              </>
            )}

            {/* Account & Settings Section */}
            <View className="px-5">
              <SectionDivider label="Account & Settings" marginTop={8} marginBottom={12} />
            </View>

            <Animated.View entering={FadeInUp.delay(650).duration(500)} className="px-5 pb-8">
              <View className="rounded-2xl overflow-hidden" style={{ backgroundColor: colors.surface.primary }}>
                <Pressable
                  onPress={() => router.push('/(investor)/portfolio')}
                  className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                  style={{ borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-4">
                    <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: colors.primary[50] }}>
                      <Briefcase size={20} color={colors.primary[600]} />
                    </View>
                    <View>
                      <Text style={{ color: colors.text.primary }} className="font-medium">Portfolio</Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">View all investments</Text>
                    </View>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>

                {PROFILE_PLANS_LINK && (
                  <Pressable
                    onPress={() => PROFILE_PLANS_LINK!.open(router, 'investor')}
                    className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                    style={{ borderColor: colors.border.light }}
                  >
                    <View className="flex-row items-center gap-4">
                      <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: TIER_COLORS[currentTier].bg }}>
                        <Crown size={20} color={TIER_COLORS[currentTier].color} />
                      </View>
                      <View>
                        <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-medium">{PROFILE_PLANS_LINK.label}</Text>
                        <Text numberOfLines={1} style={{ color: TIER_COLORS[currentTier].color }} className="text-xs font-medium">
                          {planLabel(tierConfig)}
                        </Text>
                      </View>
                    </View>
                    <ChevronRight size={20} color={colors.text.tertiary} />
                  </Pressable>
                )}

                {/* Activity log. Lives in the profile rather than the
                    dashboard: it is a record of what you have done, which is a
                    thing you go and look up, not something that belongs beside
                    the day's numbers. */}
                <Pressable
                  onPress={() => router.push('/(investor)/activity')}
                  className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                  style={{ borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-4">
                    <View
                      className="w-10 h-10 rounded-xl items-center justify-center"
                      style={{ backgroundColor: colors.surface.secondary }}
                    >
                      <History size={20} color={colors.text.secondary} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        Activity
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                      >
                        Decks received, access requested, diligences concluded
                      </Text>
                    </View>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>

                <Pressable
                  onPress={() => router.push('/(onboarding)/linkedin-verify')}
                  className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                  style={{ borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-4">
                    <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: '#0A66C215' }}>
                      <Linkedin size={20} color="#0A66C2" />
                    </View>
                    <View>
                      <Text style={{ color: colors.text.primary }} className="font-medium">LinkedIn</Text>
                      {profile.linkedInVerified ? (
                        <View className="flex-row items-center gap-1">
                          <Text style={{ color: colors.success }} className="text-xs">Connected & Verified</Text>
                          <CheckCircle size={12} color={colors.success} />
                        </View>
                      ) : (
                        <Text style={{ color: colors.warning }} className="text-xs">Not connected</Text>
                      )}
                    </View>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>

                {REFERRAL_LINK && COPY.referralProfileHint && (
                  <Pressable
                    onPress={() => REFERRAL_LINK!.open(router)}
                    className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                    style={{ borderColor: colors.border.light }}
                  >
                    <View className="flex-row items-center gap-4">
                      <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: colors.amber.light }}>
                        <Gift size={20} color={colors.warning} />
                      </View>
                      <View>
                        <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-medium">{REFERRAL_LINK.label}</Text>
                        <Text numberOfLines={1} style={{ color: colors.warning }} className="text-xs">{COPY.referralProfileHint}</Text>
                      </View>
                    </View>
                    <ChevronRight size={20} color={colors.text.tertiary} />
                  </Pressable>
                )}

                <Pressable
                  onPress={() => router.push('/settings')}
                  className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                  style={{ borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-4">
                    <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: colors.light[300] }}>
                      <Settings size={20} color={colors.text.secondary} />
                    </View>
                    <Text style={{ color: colors.text.primary }} className="font-medium">Settings</Text>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>

                <Pressable onPress={handleLogout} className="flex-row items-center justify-between p-4 active:opacity-70">
                  <View className="flex-row items-center gap-4">
                    <View className="w-10 h-10 rounded-xl items-center justify-center" style={{ backgroundColor: colors.red.light }}>
                      <LogOut size={20} color={colors.error} />
                    </View>
                    <Text style={{ color: colors.error }} className="font-medium">Sign Out</Text>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>
              </View>
            </Animated.View>
          </ScrollView>
        </ScreenFrame>
      </View>

      {/* Edit Modal */}
      <ProfileEditModal
        visible={editModal.visible}
        onClose={closeEditModal}
        onSave={handleSaveEdit}
        title={editModal.title}
        section={editModal.section}
        initialData={editModal.data}
      />
      <ReportContentModal visible={!!reportTarget} onClose={() => setReportTarget(null)} target={reportTarget} />
    </AppLayout>
  );
}
